import { Alert, Button, Card, Form, Input, Typography } from 'antd'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { forgotPassword } from '../api'

interface FormValues {
  email: string
}

/**
 * Public, unauthenticated — mounted at `/forgot-password` (linked from
 * `LoginPage`'s "Forgot password?"). Deliberately shows the exact same
 * generic success message regardless of whether the submitted email
 * exists, is unverified, or doesn't exist at all — the backend's
 * `/auth/forgot-password` response is already enumeration-safe (see
 * app/api/routes/saas_auth.py), and this page must not add a second,
 * client-side leak by e.g. branching on a 404 that never actually
 * happens for this endpoint.
 *
 * LOCAL-AUTH-A1-style: when the backend is APP_ENV=local/development it
 * may include `development_password_reset_url` so developers can finish
 * the real reset flow without a mailbox. Preview/Production never return
 * that field — this UI only shows the Local block when the API did.
 */
export default function ForgotPasswordPage() {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [message, setMessage] = useState<string>('')
  const [developmentResetUrl, setDevelopmentResetUrl] = useState<string | null>(null)

  const onFinish = async (values: FormValues) => {
    setSubmitting(true)
    try {
      const { data } = await forgotPassword(values.email.trim())
      setMessage(data.message)
      setDevelopmentResetUrl(data.development_password_reset_url?.trim() || null)
      setSubmitted(true)
    } catch {
      // Even a network/server error must not distinguish itself from a
      // normal "unknown email" outcome — show the same generic copy.
      setMessage("If an account exists for this email, we've sent a password reset link.")
      setSubmitted(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
      <Card style={{ width: 420 }}>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Vistoera Cloud Admin
        </Typography.Title>
        {submitted ? (
          <>
            <Alert type="success" message={message} showIcon style={{ margin: '16px 0' }} />
            {developmentResetUrl && (
              <div style={{ marginBottom: 16 }}>
                <Typography.Text type="secondary">
                  本地开发环境未发送真实邮件。请使用新的重置链接：
                </Typography.Text>
                <Typography.Paragraph copyable style={{ margin: '8px 0', wordBreak: 'break-all' }}>
                  {developmentResetUrl}
                </Typography.Paragraph>
                <a href={developmentResetUrl}>
                  <Button type="primary" size="small">
                    重置密码
                  </Button>
                </a>
              </div>
            )}
            <Typography.Text type="secondary">
              <Link to="/login">Back to sign in</Link>
            </Typography.Text>
          </>
        ) : (
          <>
            <Typography.Title level={4} style={{ marginTop: 16, marginBottom: 4 }}>
              Reset your password
            </Typography.Title>
            <Typography.Text type="secondary">
              Enter your email address and we'll send you a link to reset your password.
            </Typography.Text>
            <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} disabled={submitting}>
              <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }]}>
                <Input autoFocus autoComplete="username" placeholder="you@example.com" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" block loading={submitting}>
                  Send reset link
                </Button>
              </Form.Item>
              <Typography.Text type="secondary">
                <Link to="/login">Back to sign in</Link>
              </Typography.Text>
            </Form>
          </>
        )}
      </Card>
    </div>
  )
}
